import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import Breadcrumbs from "~/components/Breadcrumbs";
import FiltersBar from "~/components/FiltersBar";
import PageHeader from "~/components/PageHeader";
import api from "~/services/api";
import { getCurrentPage } from "~/functions";

import AgingCharts from "./agingCharts";
import AgingFilter from "./agingFilter";
import AgingTable from "./agingTable";
import DelinquencyAgingDetailModal from "./detailModal";
import TopStudents from "./topStudents";
import YearMatrix from "./yearMatrix";
import { formatPeriodLabel } from "./utils";

export default function ReportFinancialDelinquencyAging() {
  const { filial } = useSelector((state) => state.auth);
  const currentPage = getCurrentPage();

  const [filters, setFilters] = useState({
    period_type: "month",
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    top_size: 10,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    loadData();
  }, [filters, filial?.id]);

  function loadData() {
    setLoading(true);
    api
      .get("/reports/receivables/delinquency-aging", {
        params: {
          period_type: filters.period_type,
          year: filters.year,
          month: filters.period_type === "month" ? filters.month : undefined,
          top_size: filters.top_size,
        },
      })
      .then(({ data: response }) => {
        setData(response);
        setSelectedIndex(getDefaultPeriodIndex(response));
        setLoading(false);
      })
      .catch((err) => {
        toast(err?.response?.data?.error || "Failed to load data", {
          type: "error",
          autoClose: 3000,
        });
        setData(null);
        setLoading(false);
      });
  }

  function getDefaultPeriodIndex(response) {
    const periods = response?.periods || [];
    if (periods.length <= 1) return 0;
    const currentDate = new Date();
    if (Number(response?.year) !== currentDate.getFullYear()) {
      return periods.length - 1;
    }
    return Math.min(currentDate.getMonth(), periods.length - 1);
  }

  const periods = data?.periods || [];
  const period = periods[selectedIndex] || null;
  const isMultiPeriod = periods.length > 1;

  return (
    <div
      id="reportPage"
      className="scroll-smooth h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-y-scroll"
    >
      <PageHeader>
        <Breadcrumbs currentPage={currentPage} />
        <FiltersBar></FiltersBar>
      </PageHeader>

      <div className="w-full flex flex-col gap-4 py-4">
        <AgingFilter
          filters={filters}
          setFilters={setFilters}
          disabled={loading}
        />

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin" size={36} />
          </div>
        ) : !period ? (
          <div className="flex items-center justify-center h-64 text-sm text-gray-500">
            No data available for the selected period
          </div>
        ) : (
          <>
            {isMultiPeriod && (
              <>
                <YearMatrix
                  data={data}
                  selectedIndex={selectedIndex}
                  onSelectPeriod={setSelectedIndex}
                  onDrill={setDrill}
                />

                <div className="flex flex-row flex-wrap gap-2">
                  {periods.map((item, index) => (
                    <button
                      key={item.period}
                      type="button"
                      onClick={() => setSelectedIndex(index)}
                      className={`text-xs px-3 py-2 rounded border ${
                        index === selectedIndex
                          ? "border-mila_orange text-mila_orange"
                          : "border-transparent bg-zinc-100 text-gray-500 hover:bg-zinc-200"
                      }`}
                    >
                      {formatPeriodLabel(item.period)}
                    </button>
                  ))}
                </div>
              </>
            )}

            <AgingTable period={period} onDrill={setDrill} />

            <AgingCharts period={period} onDrill={setDrill} />

            <TopStudents
              period={period}
              topSize={filters.top_size}
              onDrill={setDrill}
            />
          </>
        )}
      </div>

      <DelinquencyAgingDetailModal
        isOpen={!!drill}
        drill={drill}
        onClose={() => setDrill(null)}
      />
    </div>
  );
}
